import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalButtonGroup,
  Popover,
  PopoverButtonGroup,
  PopoverHeader,
  PopoverBody,
  PopoverTrigger,
  Portal,
  PopoverContent,
  PopoverFooter,
  useDisclosure,
  ModalCloseButton,
} from '@frontend/components';

import {
  getGroupPrimaryKey,
  isAdmin,
  getBusinessName,
  getIsLoggedIn,
} from 'store/session/selectors';

import {
  getLastActiveGroupId,
  getLastActiveGroupBusinessName,
} from 'utils/sessionStorageUtils';
import {
  getIsShowingModal,
  getLastActiveGroupMemberId,
  lastActiveGroupSessionStorage,
} from 'utils/sessionStorageUtils';

import {
  groupMemberChangeUserGroup,
  superUserChangeUserGroup,
} from '../data/actions';

const LastActiveUserGroupModal = () => {
  const dispatch = useDispatch();
  const confirmButtonRef = useRef();
  const [isOpen, setIsOpen] = useState(getIsShowingModal());
  const {
    isOpen: isConfirmationPopoverOpen,
    onOpen: onOpenConfirmationPopover,
    onClose: onCloseConfirmationPopover,
  } = useDisclosure();

  const activeGroupId = useSelector(getGroupPrimaryKey);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const activeGroupBusinessName = useSelector(getBusinessName);
  const isSuperUser = useSelector(isAdmin);
  const handleClose = () => {
    lastActiveGroupSessionStorage.closeModal();
    setIsOpen(false);
  };

  const lastActiveGroupBusinessName = getLastActiveGroupBusinessName();
  const lastActiveGroupId = getLastActiveGroupId();
  const lastActiveGroupMemberId = getLastActiveGroupMemberId();

  useEffect(() => {
    if (isOpen) {
      if (lastActiveGroupId === activeGroupId) {
        // if the user has returned to the tab they previously were on, we automatically close it
        handleClose();
      }
    }
  }, [activeGroupId, isOpen, lastActiveGroupId]);

  useEffect(() => {
    if (isOpen && !isLoggedIn) {
      handleClose();
    }
  }, [isOpen, isLoggedIn]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <ModalHeader>
          Active organization changed
          <ModalCloseButton />
        </ModalHeader>
        {isSuperUser ? (
          <ModalBody>
            The contents of this page have changed because your active
            organization changed to&nbsp;
            <b>{activeGroupBusinessName}</b>.<br />
          </ModalBody>
        ) : (
          <ModalBody>
            The contents of this page have changed because your active
            organization changed from&nbsp;
            <b>{lastActiveGroupBusinessName}</b> to{' '}
            <b>{activeGroupBusinessName}</b>.<br />
          </ModalBody>
        )}
        <ModalFooter>
          <ModalButtonGroup>
            <Button onClick={handleClose} variant="secondary">
              close
            </Button>
            <Popover
              initialFocusRef={confirmButtonRef}
              isOpen={isConfirmationPopoverOpen}
              onClose={onCloseConfirmationPopover}
            >
              <PopoverTrigger>
                <Button
                  colorScheme="secondary"
                  onClick={onOpenConfirmationPopover}
                >
                  Return to original organization
                </Button>
              </PopoverTrigger>
              <Portal>
                <PopoverContent>
                  <PopoverHeader>Change active organization?</PopoverHeader>
                  <PopoverBody>
                    Changing your active organization will affect all tabs in
                    your browser.
                  </PopoverBody>
                  <PopoverFooter>
                    <PopoverButtonGroup>
                      <Button
                        variant="secondary"
                        mr="1"
                        onClick={onCloseConfirmationPopover}
                      >
                        cancel
                      </Button>
                      <Button
                        variant="primary"
                        ref={confirmButtonRef}
                        onClick={() => {
                          if (isSuperUser) {
                            dispatch(
                              superUserChangeUserGroup.request({
                                userGroupId: lastActiveGroupId,
                              }),
                            );
                          } else {
                            dispatch(
                              groupMemberChangeUserGroup.request({
                                groupMemberId: lastActiveGroupMemberId,
                              }),
                            );
                          }
                          onCloseConfirmationPopover();
                          handleClose();
                        }}
                      >
                        confirm
                      </Button>
                    </PopoverButtonGroup>
                  </PopoverFooter>
                </PopoverContent>
              </Portal>
            </Popover>
          </ModalButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LastActiveUserGroupModal;
